import { Nullable } from '@tcc/common';
import { ReportMeasurement, YieldRegionReport } from './Reports';

export interface ArdFieldRegionReportBaseStats {
  dryMass: Nullable<ReportMeasurement>;
  harvestedArea: Nullable<ReportMeasurement>;
  moistureMass?: Nullable<ReportMeasurement>;
  moisturePercent?: Nullable<ReportMeasurement>;
  nominalWeight?: Nullable<ReportMeasurement>;
  stdYield: Nullable<ReportMeasurement>;
  wetMass?: Nullable<ReportMeasurement>;
}

export interface BySeedProductDimension {
  seedProduct: {
    id: string;
    name?: string;
  };
  cropNominalMoisture?: Nullable<ReportMeasurement>;
  cropNominalWeight?: Nullable<ReportMeasurement>;
  stats: ArdFieldRegionReportBaseStats;
}

export interface ByRangeDimension {
  min: number | null;
  max: number | null;
  units?: string;
  stats: ArdFieldRegionReportBaseStats;
}

export interface ByDateDimension {
  date: string | null;
  stats: ArdFieldRegionReportBaseStats;
}

export type RootLevelBySeedProductDimension = BySeedProductDimension & {
  dimensions: {
    byElevation: ByRangeDimension[];
    byPopulation: ByRangeDimension[];
    byPlantingDate: ByDateDimension[];
  }
}

export type RootLevelByElevationDimension = ByRangeDimension & {
  dimensions: {
    bySeedProduct: BySeedProductDimension[];
    byPopulation: ByRangeDimension[];
    byPlantingDate: ByDateDimension[];
  }
}

export type RootLevelByPopulationDimension = ByRangeDimension & {
  dimensions: {
    bySeedProduct: BySeedProductDimension[];
    byElevation: ByRangeDimension[];
    byPlantingDate: ByDateDimension[];
  }
}

export type RootLevelByPlantingDateDimension = ByDateDimension & {
  dimensions: {
    bySeedProduct: BySeedProductDimension[];
    byElevation: ByRangeDimension[];
    byPopulation: ByRangeDimension[];
  }
}

export interface ArdFieldRegionReport {
  stats: ArdFieldRegionReportBaseStats;
  dimensions: {
    bySeedProduct: RootLevelBySeedProductDimension[];
    byElevation: RootLevelByElevationDimension[];
    byPopulation: RootLevelByPopulationDimension[];
    byPlantingDate: RootLevelByPlantingDateDimension[];
  }
}

export type RootLevelDimensions =
  ArdFieldRegionReport
  | RootLevelBySeedProductDimension
  | RootLevelByElevationDimension
  | RootLevelByPopulationDimension
  | RootLevelByPlantingDateDimension

export type RootLevelDimensionsList = 
  RootLevelBySeedProductDimension[]
  | RootLevelByElevationDimension[]
  | RootLevelByPopulationDimension[]
  | RootLevelByPlantingDateDimension[];

export type LevelTwoDimensionsList =
  BySeedProductDimension[]
  | ByRangeDimension[]
  | ByDateDimension[];

export type RgbColor = {
  red: number;
  green: number;
  blue: number;
  alpha?: Nullable<number>;
}
export type ColorsMap =  Record<string, RgbColor>;

export const isArdFieldRegionReport = (report: unknown): report is ArdFieldRegionReport => 
  Boolean(report && typeof report === 'object' && ('dimensions' in report || 'stats' in report));

export const isBySeedProductItem = (dimension: unknown): dimension is BySeedProductDimension =>
  Boolean(dimension && typeof dimension === 'object' && 'seedProduct' in dimension);

export const isByRangeDimensionItem = (dimension: unknown): dimension is ByRangeDimension =>
  Boolean(dimension && typeof dimension === 'object' && 'min' in dimension && 'max' in dimension);

export const isDateDimensionItem = (dimension: unknown): dimension is ByDateDimension =>
  Boolean(dimension && typeof dimension === 'object' && 'date' in dimension);

export const isYieldRegionReport = (item: unknown): item is YieldRegionReport =>
  Boolean(item && typeof item === 'object' && 'label' in item) && isArdFieldRegionReport(item) === false;
